/**
 * Top progress bar for route changes.
 *
 * Starts the moment the visitor **clicks an internal link** (or uses Back /
 * Forward) and completes as soon as the router commits the new location, so
 * every route change has a visible “something is happening” line instead of a
 * frozen screen. Internal links are detected with one capture-phase click
 * listener (same origin, no modifier keys, no `target=_blank`, no `#anchor`),
 * so pages do not have to wire anything up.
 *
 * The bar is 3px tall, fixed at the very top and pointer-events:none — it never
 * blocks a click. Brand gradient (community blue → Mini Shop orange).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BLUE, ORANGE } from './community/brand';

const TICK = 180;          // ms between increments while loading
const MIN_VISIBLE = 260;   // ms — never flash for a single frame

export default function RouteProgress() {
  const { pathname, search } = useLocation();
  const [value, setValue] = useState(0);
  const [visible, setVisible] = useState(false);
  const running = useRef(false);       // a navigation is in flight
  const tick = useRef(null);
  const finish = useRef(null);
  const startedAt = useRef(0);
  const first = useRef(true);

  const clearTimers = useCallback(() => {
    if (tick.current) { clearInterval(tick.current); tick.current = null; }
    if (finish.current) { clearTimeout(finish.current); finish.current = null; }
  }, []);

  /** A click / Back was seen — assume a route change is coming. */
  const start = useCallback(() => {
    if (running.current) return;
    running.current = true;
    startedAt.current = Date.now();
    if (finish.current) { clearTimeout(finish.current); finish.current = null; }
    setVisible(true);
    setValue(10);
    tick.current = setInterval(() => {
      // Ease towards 92% — the last 8% is reserved for the real navigation.
      setValue((v) => (v >= 92 ? v : v + Math.max(1, Math.round((92 - v) * 0.18))));
    }, TICK);
  }, []);

  /** The router committed the new location — finish and fade out. */
  const done = useCallback(() => {
    if (!running.current) return;
    running.current = false;
    clearTimers();
    setValue(100);
    const wait = Math.max(0, MIN_VISIBLE - (Date.now() - startedAt.current));
    finish.current = setTimeout(() => {
      setVisible(false);
      setValue(0);
      finish.current = null;
    }, wait + 180);
  }, [clearTimers]);

  // Every internal navigation starts the bar.
  useEffect(() => {
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target?.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.hasAttribute('download')) return;
      const target = anchor.getAttribute('target');
      if (target && target !== '_self') return;
      const href = anchor.getAttribute('href') || '';
      if (!href || href.startsWith('#') || /^(mailto|tel|javascript):/i.test(href)) return;
      let url;
      try { url = new URL(anchor.href, window.location.origin); } catch (err) { return; }
      if (url.origin !== window.location.origin) return;                 // external site
      if (url.pathname === pathname && url.search === search) return;    // same page
      start();
    };
    const onHistory = () => start();
    document.addEventListener('click', onClick, true);
    window.addEventListener('popstate', onHistory);
    return () => {
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('popstate', onHistory);
      clearTimers();
    };
  }, [pathname, search, start, clearTimers]);

  // Location changed (including the very first render, which is skipped).
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    done();
  }, [pathname, search, done]);

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none" style={{ height: 3 }}>
      <div
        style={{
          height: '100%',
          width: `${value}%`,
          opacity: visible ? 1 : 0,
          background: `linear-gradient(90deg, ${BLUE} 0%, ${ORANGE} 100%)`,
          boxShadow: '0 0 10px rgba(11, 95, 219, 0.55)',
          transition: visible ? 'width .25s ease' : 'width .2s ease, opacity .28s ease',
        }}
      />
    </div>
  );
}
